import React, { useState } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { Loader2, MailWarning, X } from 'lucide-react';

function VerificationBanner({ email }) {
  const { resendVerification } = useAuth();
  const [dismissed, setDismissed] = useState(false);
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  if (dismissed) return null;

  const handleResend = async () => {
    setSending(true);
    await resendVerification(email);
    setSending(false);
    setSent(true);
  };

  return (
    <div className="bg-yellow-50 border-b border-yellow-200 px-4 py-3 flex items-center justify-between gap-4">
      <div className="flex items-center gap-3 text-sm text-yellow-800">
        <MailWarning className="w-4 h-4 flex-shrink-0" />
        {sent ? (
          <span>Verification email sent — check your inbox.</span>
        ) : (
          <span>
            Please verify your email address.{' '}
            <button
              onClick={handleResend}
              disabled={sending}
              className="font-medium underline hover:no-underline disabled:opacity-50"
            >
              {sending ? 'Sending…' : 'Resend email'}
            </button>
          </span>
        )}
      </div>
      <button
        onClick={() => setDismissed(true)}
        className="text-yellow-600 hover:text-yellow-800 flex-shrink-0"
        aria-label="Dismiss"
      >
        <X className="w-4 h-4" />
      </button>
    </div>
  );
}

export default function PrivateRoute({ children }) {
  const { isAuthenticated, loading, user } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-8 h-8 animate-spin mx-auto" style={{ color: '#CC0202' }} />
          <p className="mt-2 text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/onboarding" state={{ from: location }} replace />;
  }

  if (user && !user.onboardingComplete && location.pathname !== '/onboarding') {
    return <Navigate to="/onboarding" replace />;
  }

  const showBanner = user && !user.emailVerified && user.authProvider === 'email';

  return (
    <>
      {showBanner && <VerificationBanner email={user.email} />}
      {children}
    </>
  );
}
